import React, { ReactFragment } from 'react';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import { RoutePath } from 'enums/Routes';
import { useSelector } from 'react-redux';
import { getApplicationData } from 'selectors/getApplicationData';
import { GetApplicationData } from 'handlers/applicationData';
import useLayoutTheme from 'hooks/useLayoutTheme';

import Expander from 'components/Expander';

import styles from './CommonQuestions.module.scss';

interface Question {
  question: string;
  answer: string | ReactFragment;
}

const privacyPolicyLink = (
  <a href="https://www.planneryapp.com/privacy-policy" target="_blank" rel="noreferrer">
    Privacy Policy
  </a>
);

const infoSecure = {
  question: 'How do you keep my information secure?',
  answer: (
    <p>
      Our information is transferred and stored using the latest encryption standards. We do not sell your information,
      or share any information with third parties other than those required to provide you with our service (for
      example, looking up your credit report). To learn more, please read our {privacyPolicyLink}.
    </p>
  ),
};

const whatCreditScore = {
  question: 'What credit score will I need to be approved?',
  answer: `We accept applicants with credit scores of 580 and above. \
  Credit scores can vary depending on the bureau, and can change frequently. \
  Your credit score might be higher than you think, so it doesn't hurt to apply and find out!`,
};

const isCreditAffected = {
  question: 'Will applying affect my credit?',
  answer: `We do a soft pull to confirm your liabilities. Your score will not be affected.`,
};

const getQuestions = (path: RoutePath, application: GetApplicationData | undefined): Question[] => {
  switch (path) {
    case RoutePath.Offer:
      return [
        {
          question: 'How long does it take to apply?',
          answer: 'Our application takes less than 5 minutes for you to complete and receive a pre-approved offer.',
        },
        {
          question: 'When do I get my loan money?',
          answer:
            'After you sign the loan documents and set up your payment process, you could receive the funding within one to three business days.',
        },
        {
          question: 'How are you able to offer lower rates compared to other lenders?',
          answer: (
            <>
              <p>
                Unlike other lenders, we are solely focused on serving one community - healthcare professionals.
                Understanding the profile of healthcare professionals and addressing their needs distinguishes us from
                the competition.
              </p>
              <p>
                By taking repayments directly from your paycheck allows we are able to offer lower rates while helping
                you to maintain on-time payments.
              </p>
            </>
          ),
        },
        whatCreditScore,
        isCreditAffected,
      ];
    case RoutePath.YourName:
      return [infoSecure];
    case RoutePath.YourAddress:
      return [
        {
          question: 'How do you use this information?',
          answer: (
            <p>
              We use your address to verify your identity and that you are in an eligible area that we serve. To learn
              more about how we use the information we collect, please read our {privacyPolicyLink}.
            </p>
          ),
        },
        {
          question: 'Will you send me any mail?',
          answer: 'We do not send any mail to the address you provide. We use your address to verify your identity.',
        },
        infoSecure,
      ];
    case RoutePath.YourTotalIncome:
      return [
        {
          question: 'How does this affect my offer?',
          answer: `Your income is used to verify that you make enough money to have a reasonable chance of paying back your debt. This does not affect your chance of approval for the vast majority of cases. Your income also does not affect your rate.`,
        },
        infoSecure,
      ];
    case RoutePath.YourCreditReport:
      return [
        isCreditAffected,
        whatCreditScore,
        {
          question: 'How do you use this information?',
          answer: `The credit report helps us to determine your eligibility for funding and to create your specific offer.`,
        },
        infoSecure,
      ];
    case RoutePath.ConfirmLoan:
      return [
        {
          question: 'When do I sign the final loan agreement?',
          answer: 'You will sign the final loan agreement after we verify your employment, and repayment is set up.',
        },
        {
          question: 'Why is there an origination fee for the loan?',
          answer:
            'The origination fee covers our cost of offering you the loan. We add this fee on to the total loan amount, so you don’t have to pay anything upfront.',
        },
        {
          question: 'What fees do you charge?',
          answer:
            'We charge an origination fee, and in certain states, there is a mandatory fee we must pay to the state. There is also a fee for missed payments, but we will set you up with automatic payments to avoid this. All of the fees are disclosed in the loan agreement.',
        },
        {
          question: 'How long is this offer valid for?',
          answer:
            'This offer is valid for 30 days from the date that the offer was generated. After that time, you’ll have to reapply to get a new offer. ',
        },
      ];
    case RoutePath.RepaymentMethod:
      return [
        {
          question: 'Do I have to open this account to get the loan?',
          answer:
            'No. Having the direct deposit account makes it much easier to make payments for the loan. However, if you do not want to set this up, please call or text us at 877-643-3131.',
        },
        {
          question: 'Why can’t I use my existing bank account?',
          answer:
            'This bank account is created solely for the purpose of depositing and transferring payments for your Plannery loan. By having this as a separate bank account, we are able to set up the automation necessary to pay back the loan from your paycheck. If you do not want to set up this account, please call or text us at 877-643-3131.',
        },
        {
          question: 'Can I use this as a checking account?',
          answer:
            'No. This account is solely for the purpose of depositing and transferring payments for your Plannery loan. We don’t offer any other banking features at this time.',
        },
        {
          question: 'Are there any fees associated with the account?',
          answer:
            'Yes, there is a return-deposited item fee of $25, a paper statement fee (per statement) of $5 if requested or not opted into electronic statements. There is no minimum balance requirement. Since this account is solely for the purpose of depositing and transferring payments for your Plannery loan, the majority of the time your account will have zero balance.',
        },
        {
          question: 'Will I get statements?',
          answer: 'Yes. We will provide monthly account statements.',
        },
        {
          question: 'How will I be able to close the account in the future?',
          answer:
            'Your account will be closed automatically when your loan is fully repaid. You may also give us a call if you would like to close your account.',
        },
        {
          question: 'What happens if my employer changes?',
          answer:
            'If your employer changes, you will reconnect the loan to your new employer’s payroll system. We can also update your loan to match your new employer’s pay cycle.',
        },
        {
          question: 'What happens if I lose my job?',
          answer:
            'If payroll doesn’t run during a pay period, we will automatically deduct the money from your checking account that you provided earlier. We know life happens. If you are unable to pay, give us a call as soon as you can.',
        },
        ...(application?.fullDepositSwitch
          ? [
              {
                question: 'Will this change when I get access to my paycheck?',
                answer:
                  'Once your paycheck is deposited to your Plannery deposit account and your loan payment is debited, the remaining balance will be sent to your external checking account.  As long as cutoff times are met, you will have access to your money that same day.',
              },
            ]
          : []),
      ];
    default:
      return [];
  }
};

const CommonQuestions = () => {
  const location = useLocation();
  const { application } = useSelector(getApplicationData);
  const { themeStyles } = useLayoutTheme();

  const questions = getQuestions(location.pathname as RoutePath, application);

  if (questions.length === 0) {
    return <></>;
  }

  return (
    <div className={clsx(styles.commonQuestions, themeStyles.commonQuestions)}>
      <h4 className={styles.heading}>Common questions:</h4>
      <div className={clsx(styles.questions, themeStyles.questions)}>
        {questions.map((question: Question) => (
          <Expander
            key={question.question}
            container={styles.expanderContainer}
            label={question.question}
            labelClassname={styles.label}
            content={question.answer}
            selectArrowClassname={clsx(styles.selectArrow, themeStyles.selectArrow)}
            openClassname={styles.expanderOpen}
          />
        ))}
      </div>
    </div>
  );
};

export default CommonQuestions;
